import { EmptyState, Input, Skeleton, Tabs, When } from '@/components';
import { formatDateTime } from '@/lib/datetime';
import { For, Show, createSignal, onCleanup } from 'solid-js';
import { useGitBranches, useGitCommits, useGitPulls, useGitTags, useReleaseCommit } from './hooks';

export interface RefSelection {
  /** Branch/tag name Confirm dispatches on; '' when a bare commit was picked. */
  ref: string;
  /** Pinned commit SHA the workflows must build. */
  commitSha: string;
  label: string;
}

const SHA_INPUT_PATTERN = /^[0-9a-f]{7,40}$/;
const SEARCH_DEBOUNCE_MS = 300;

/** Brand-tinted two-line tile — the shared look for every "selected item" list (UCH-435). */
export const selectedTileClass =
  'rounded-lg border border-brand-200 bg-brand-50/50 px-3 py-2 text-sm transition-colors duration-200 ease-out hover:bg-brand-50 motion-reduce:transition-none dark:border-brand-900 dark:bg-brand-900/20 dark:hover:bg-brand-900/30';

/**
 * The pinned selection, shown apart from the pick lists for clarity. Resolves
 * the commit's message/date itself so callers can place it anywhere.
 */
export const SelectedRefCard = (props: {
  productId: string;
  selection: RefSelection;
  /** Commit page link (e.g. on GitHub); the SHA renders as a link when set. */
  href?: string;
  /** Message to show until (or unless) GitHub returns the full one, e.g. a frozen capture. */
  subject?: string;
  /** Read-only use: look the commit up through the release (view permission) instead of the picker routes. */
  releaseId?: string;
}) => {
  const viaRelease = useReleaseCommit(() => props.releaseId ?? '');
  const viaPicker = useGitCommits(() => ({
    productId: props.releaseId ? '' : props.productId,
    sha: props.selection.commitSha,
  }));
  const commit = () =>
    props.releaseId ? viaRelease.data?.data : (viaPicker.data?.data[0] ?? null);
  const date = () => commit()?.date;
  const sha = () => <code class="break-all">{props.selection.commitSha}</code>;
  return (
    <div class={selectedTileClass}>
      <div class="flex items-center justify-between gap-2">
        <span class="truncate font-medium">{props.selection.label}</span>
        <Show when={date()}>
          {(d) => (
            <span class="flex shrink-0 items-center gap-1.5 text-xs text-neutral-500">
              <span>{formatDateTime(d())}</span>
              <When date={d()} />
            </span>
          )}
        </Show>
      </div>
      <div class="mt-1 text-xs text-neutral-500">
        <Show when={props.href} fallback={sha()}>
          {(href) => (
            <a
              href={href()}
              target="_blank"
              rel="noreferrer"
              class="text-brand-600 hover:underline dark:text-brand-400"
            >
              {sha()}
            </a>
          )}
        </Show>
      </div>
      <Show when={commit()?.message || props.subject}>
        {(message) => (
          <p class="mt-1 whitespace-pre-wrap break-words text-xs text-neutral-600 dark:text-neutral-300">
            {message()}
          </p>
        )}
      </Show>
    </div>
  );
};

const TABS = [
  { id: 'pr', label: 'PR' },
  { id: 'commit', label: 'Commit' },
  { id: 'branch', label: 'Branch' },
  { id: 'tag', label: 'Tag' },
] as const;

const Row = (props: { selected: boolean; onSelect: () => void; children: unknown }) => (
  <li>
    <button
      type="button"
      onClick={props.onSelect}
      class={`w-full rounded-lg px-2 py-1.5 text-left text-sm transition-colors duration-200 ease-out hover:bg-neutral-100 motion-reduce:transition-none dark:hover:bg-neutral-800 ${
        props.selected ? 'bg-brand-50 dark:bg-brand-900/30' : ''
      }`}
    >
      {props.children as never}
    </button>
  </li>
);

/**
 * GitHub-backed ref picker (UCH-239): PR / Commit / Branch / Tag tabs. Every
 * selection pins an exact commit SHA, shown with its message below the list.
 */
export const RefPicker = (props: {
  productId: string;
  value: RefSelection | null;
  onChange: (selection: RefSelection) => void;
}) => {
  const [tab, setTab] = createSignal<string>('pr');
  const [search, setSearch] = createSignal('');
  // Queries key off the debounced value so each keystroke doesn't refetch.
  const [query, setQuery] = createSignal('');
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  const updateSearch = (value: string) => {
    setSearch(value);
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => setQuery(value), SEARCH_DEBOUNCE_MS);
  };
  onCleanup(() => clearTimeout(debounceTimer));

  const pulls = useGitPulls(() => (tab() === 'pr' ? props.productId : ''));
  const shaLookup = () => SHA_INPUT_PATTERN.test(query().trim().toLowerCase());
  const commits = useGitCommits(() => ({
    productId: tab() === 'commit' ? props.productId : '',
    sha: shaLookup() ? query().trim().toLowerCase() : undefined,
  }));
  const branches = useGitBranches(() => ({
    productId: tab() === 'branch' ? props.productId : '',
    q: query().trim() || undefined,
  }));
  const tags = useGitTags(() => ({
    productId: tab() === 'tag' ? props.productId : '',
    q: query().trim() || undefined,
  }));

  const pullList = () =>
    (pulls.data?.data ?? []).filter((pr) =>
      `#${pr.number} ${pr.title} ${pr.headRef}`
        .toLowerCase()
        .includes(search().trim().toLowerCase()),
    );

  const commitList = () =>
    shaLookup()
      ? (commits.data?.data ?? [])
      : (commits.data?.data ?? []).filter(
          (c) =>
            !search().trim() || c.message.toLowerCase().includes(search().trim().toLowerCase()),
        );

  const active = () => {
    switch (tab()) {
      case 'pr':
        return pulls;
      case 'commit':
        return commits;
      case 'branch':
        return branches;
      default:
        return tags;
    }
  };

  return (
    <div class="rounded-xl border border-neutral-200 bg-surface p-3 dark:border-neutral-800 dark:bg-surface-dark">
      <Tabs
        tabs={TABS}
        active={tab()}
        onChange={(t) => {
          setTab(t);
          updateSearch('');
        }}
        class="mb-2"
      />
      <Input
        value={search()}
        onInput={(e) => updateSearch(e.currentTarget.value)}
        placeholder={
          tab() === 'commit'
            ? 'Search message or paste a SHA'
            : tab() === 'pr'
              ? 'Search PR number, title or branch'
              : 'Search part of a name…'
        }
        class="mb-2"
        aria-label="Search refs"
      />

      <Show when={!active().isPending} fallback={<Skeleton class="h-24" />}>
        <ul class="flex max-h-56 flex-col gap-0.5 overflow-y-auto">
          <Show when={tab() === 'pr'}>
            <For
              each={pullList()}
              fallback={
                <EmptyState
                  title={search().trim() ? 'No matching pull requests' : 'No open pull requests'}
                  class="py-3"
                />
              }
            >
              {(pr) => (
                <Row
                  selected={props.value?.commitSha === pr.headSha}
                  onSelect={() =>
                    props.onChange({
                      ref: pr.headRef,
                      commitSha: pr.headSha,
                      label: `PR #${pr.number} ${pr.title}`,
                    })
                  }
                >
                  <span class="flex items-center justify-between gap-2">
                    <span class="truncate">
                      <span class="font-medium">#{pr.number}</span> {pr.title}
                    </span>
                    <When date={pr.updatedAt} />
                  </span>
                  <span class="block text-xs text-neutral-500">{pr.headRef}</span>
                </Row>
              )}
            </For>
          </Show>
          <Show when={tab() === 'commit'}>
            <For each={commitList()} fallback={<EmptyState title="No commits" class="py-3" />}>
              {(c) => (
                <Row
                  selected={props.value?.commitSha === c.sha}
                  onSelect={() =>
                    props.onChange({ ref: '', commitSha: c.sha, label: c.sha.slice(0, 7) })
                  }
                >
                  <span class="flex items-center justify-between gap-2">
                    <span class="truncate">
                      <code class="text-xs text-neutral-500">{c.sha.slice(0, 7)}</code>{' '}
                      {c.message.split('\n')[0]}
                    </span>
                    <When date={c.date} />
                  </span>
                </Row>
              )}
            </For>
          </Show>
          <Show when={tab() === 'branch'}>
            <For
              each={branches.data?.data ?? []}
              fallback={<EmptyState title="No branches" class="py-3" />}
            >
              {(b) => (
                <Row
                  selected={props.value?.commitSha === b.sha}
                  onSelect={() => props.onChange({ ref: b.name, commitSha: b.sha, label: b.name })}
                >
                  <span class="flex items-center justify-between gap-2">
                    <span class="truncate">
                      {b.name}
                      <code class="ml-2 text-xs text-neutral-500">{b.sha.slice(0, 7)}</code>
                    </span>
                    <When date={b.latestCommit?.date} />
                  </span>
                  <Show when={b.latestCommit}>
                    {(commit) => (
                      <span class="block truncate text-xs text-neutral-500">
                        {commit().message.split('\n')[0]}
                      </span>
                    )}
                  </Show>
                </Row>
              )}
            </For>
          </Show>
          <Show when={tab() === 'tag'}>
            <For
              each={tags.data?.data ?? []}
              fallback={<EmptyState title="No tags" class="py-3" />}
            >
              {(t) => (
                <Row
                  selected={props.value?.commitSha === t.sha}
                  onSelect={() => props.onChange({ ref: t.name, commitSha: t.sha, label: t.name })}
                >
                  <span class="flex items-center justify-between gap-2">
                    <span class="truncate">
                      {t.name}
                      <code class="ml-2 text-xs text-neutral-500">{t.sha.slice(0, 7)}</code>
                    </span>
                    <When date={t.latestCommit?.date} />
                  </span>
                  <Show when={t.latestCommit}>
                    {(commit) => (
                      <span class="block truncate text-xs text-neutral-500">
                        {commit().message.split('\n')[0]}
                      </span>
                    )}
                  </Show>
                </Row>
              )}
            </For>
          </Show>
        </ul>
      </Show>
    </div>
  );
};
